import React, { useEffect, useMemo, useState } from "react";
import api, { formatIDR } from "../lib/api";
import Layout from "../components/Layout";
import { ArrowDownCircle, ArrowUpCircle, Pencil, Trash2, Search, PiggyBank, Lock, KeyRound, Landmark } from "lucide-react";
import { toast } from "sonner";

// Petty Cash Medan: dana yang dipegang Pak Syahlan. Dana masuk otomatis dari order Tunai yang pemegang uangnya
// "Pak Syahlan" (dikelola dari order), plus input manual. Dana keluar per kategori Pengeluaran + "Disetor ke Pusat".
const today = () => new Date().toISOString().slice(0, 10);
const emptyIn = () => ({ type: "in", date: today(), amount: 0, note: "", order_id: "", category: "" });
const emptyOut = () => ({ type: "out", date: today(), amount: 0, note: "", order_id: "", category: "" });

const PettyCashMedan = () => {
  const [token, setToken] = useState("");
  const [lockPwd, setLockPwd] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [data, setData] = useState({ entries: [], balance: 0, total_in: 0, total_out: 0, total_setor: 0, own_password: false });
  const [cats, setCats] = useState([]);
  const [setorCat, setSetorCat] = useState("Disetor ke Pusat");
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyIn());
  const [editingId, setEditingId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pwdModal, setPwdModal] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: "", new_password: "", confirm: "" });

  const cfg = () => ({ headers: { "X-PCM-Token": token } });
  const onAuthError = (err) => {
    if (err?.response?.status === 403) { setToken(""); toast.error("Sesi Petty Cash Medan berakhir — masukkan password lagi"); return true; }
    return false;
  };

  const submitUnlock = async (e) => {
    e.preventDefault();
    setUnlocking(true);
    try {
      const { data: d } = await api.post("/petty-cash-medan/unlock", { password: lockPwd });
      setToken(d.token);
      setLockPwd("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password salah");
    } finally { setUnlocking(false); }
  };

  const load = async () => {
    const params = {};
    if (filterType) params.type = filterType;
    if (start) params.start = start;
    if (end) params.end = end;
    try {
      const { data: d } = await api.get("/petty-cash-medan", { params, ...cfg() });
      setData(d);
    } catch (err) { if (!onAuthError(err)) toast.error(err?.response?.data?.detail || "Gagal memuat data"); }
  };
  const loadRefs = async () => {
    try {
      const [c, o] = await Promise.all([api.get("/petty-cash-medan/categories", cfg()), api.get("/orders")]);
      setCats(c.data.categories || []);
      setSetorCat(c.data.setor || "Disetor ke Pusat");
      setOrders(o.data || []);
    } catch (err) { onAuthError(err); }
  };

  useEffect(() => { if (token) loadRefs(); /* eslint-disable-next-line */ }, [token]);
  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [token, filterType, start, end]);

  const filtered = useMemo(() => {
    if (!search) return data.entries;
    const s = search.toLowerCase();
    return data.entries.filter((e) => [e.category, e.note, e.order?.order_no, e.order?.customer_name].some((v) => (v || "").toLowerCase().includes(s)));
  }, [search, data.entries]);

  const openIn = () => { setForm(emptyIn()); setEditingId(null); setModal(true); };
  const openOut = () => { setForm(emptyOut()); setEditingId(null); setModal(true); };
  const openSetor = () => { setForm({ ...emptyOut(), category: setorCat }); setEditingId(null); setModal(true); };
  const openEdit = (e) => {
    setForm({ type: e.type, date: e.date, amount: e.amount, note: e.note || "", order_id: e.order_id || "", category: e.category || "" });
    setEditingId(e.id); setModal(true);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const payload = { type: form.type, date: form.date, amount: Number(form.amount), note: form.note, category: form.type === "out" ? form.category : "", order_id: form.order_id || "" };
    setSaving(true);
    try {
      if (editingId) await api.put(`/petty-cash-medan/${editingId}`, payload, cfg());
      else await api.post("/petty-cash-medan", payload, cfg());
      toast.success(form.type === "in" ? "Dana masuk tersimpan" : form.category === setorCat ? "Setoran ke pusat tersimpan" : "Dana keluar tersimpan (juga tercatat di Pengeluaran)");
      setModal(false); load();
    } catch (err) {
      if (!onAuthError(err)) toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/petty-cash-medan/${confirmDel.id}`, cfg());
      toast.success("Data dihapus");
    } catch (err) { if (!onAuthError(err)) toast.error(err?.response?.data?.detail || "Gagal menghapus"); }
    setConfirmDel(null); load();
  };

  const changePassword = async (ev) => {
    ev.preventDefault();
    if (pwdForm.new_password !== pwdForm.confirm) { toast.error("Konfirmasi password baru tidak sama"); return; }
    try {
      await api.put("/petty-cash-medan/password", { current_password: pwdForm.current_password, new_password: pwdForm.new_password }, cfg());
      toast.success("Password Petty Cash Medan diganti");
      setPwdModal(false); setPwdForm({ current_password: "", new_password: "", confirm: "" }); load();
    } catch (err) { if (!onAuthError(err)) toast.error(err?.response?.data?.detail || "Gagal mengganti password"); }
  };

  const entryOrderLabel = (e) => {
    if (e.order) return `${e.order.order_no} · ${e.order.customer_name || ""}`;
    if (!e.order_id) return "—";
    const o = orders.find((x) => x.id === e.order_id);
    return o ? `${o.order_no} · ${o.customer?.name || ""}` : "—";
  };

  if (!token) {
    return (
      <Layout>
        <div className="max-w-md mx-auto mt-12 bg-white border border-[#E4E4E7]" data-testid="pcm-lock-screen">
          <div className="px-6 py-4 border-b border-[#E4E4E7]">
            <div className="label-cap">Akses Terproteksi</div>
            <h2 className="font-heading text-2xl font-bold">Petty Cash Medan</h2>
          </div>
          <form onSubmit={submitUnlock} className="p-6 space-y-4">
            <p className="text-sm text-[#52525B]">Halaman ini diproteksi. Masukkan password Petty Cash Medan (bila belum pernah diganti, sama dengan password pengaturan).</p>
            <input type="password" required autoFocus value={lockPwd} onChange={(e) => setLockPwd(e.target.value)} placeholder="Password" data-testid="pcm-unlock-input" className="w-full px-3 py-2.5 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] font-mono" />
            <button type="submit" disabled={unlocking} data-testid="pcm-unlock-button" className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold uppercase tracking-wider px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-60">
              <Lock size={14} /> {unlocking ? "Memverifikasi…" : "Buka Petty Cash Medan"}
            </button>
          </form>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-cap mb-2">Dana Pak Syahlan · Wilayah Sumatra</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Petty Cash Medan</h1>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <button onClick={() => setPwdModal(true)} title="Ganti password" data-testid="pcm-change-password" className="border border-[#E4E4E7] hover:bg-[#F4F4F5] text-xs font-bold uppercase tracking-wider px-3 py-2.5 flex items-center gap-2">
            <KeyRound size={14} /> Password
          </button>
          <button onClick={openIn} data-testid="pcm-add-in" className="bg-[#059669] hover:bg-[#047857] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 flex items-center gap-2">
            <ArrowDownCircle size={14} /> Dana Masuk
          </button>
          <button onClick={openOut} data-testid="pcm-add-out" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 flex items-center gap-2">
            <ArrowUpCircle size={14} /> Dana Keluar
          </button>
          <button onClick={openSetor} data-testid="pcm-add-setor" className="bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 flex items-center gap-2">
            <Landmark size={14} /> Setor ke Pusat
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="border p-4 bg-[#111113] border-[#111113] text-white col-span-2 lg:col-span-1">
          <div className="label-cap text-white/70 flex items-center gap-1.5"><PiggyBank size={12} /> Saldo (dipegang)</div>
          <div className="font-mono font-black text-2xl mt-1" data-testid="pcm-balance">{formatIDR(data.balance)}</div>
          <div className="text-[10px] text-white/70 mt-1">Masuk − keluar (termasuk setoran)</div>
        </div>
        <div className="bg-white border border-[#E4E4E7] p-4">
          <div className="label-cap text-[#059669]">Total Masuk</div>
          <div className="font-mono font-bold text-xl mt-1">{formatIDR(data.total_in)}</div>
        </div>
        <div className="bg-white border border-[#E4E4E7] p-4">
          <div className="label-cap text-[#DC2626]">Total Pengeluaran</div>
          <div className="font-mono font-bold text-xl mt-1">{formatIDR((data.total_out || 0) - (data.total_setor || 0))}</div>
        </div>
        <div className="bg-white border border-[#E4E4E7] p-4">
          <div className="label-cap text-[#002FA7]">Disetor ke Pusat</div>
          <div className="font-mono font-bold text-xl mt-1">{formatIDR(data.total_setor)}</div>
        </div>
      </div>

      <div className="bg-white border border-[#E4E4E7] p-4 mb-4 flex items-end gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[200px] px-2">
          <Search size={14} className="text-[#52525B]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari catatan/kategori/order…" className="flex-1 outline-none text-sm bg-transparent" data-testid="pcm-search" />
        </div>
        <div>
          <label className="label-cap block mb-1">Jenis</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-2 py-1.5 border border-[#E4E4E7] text-xs bg-white" data-testid="pcm-type-filter">
            <option value="">Semua</option>
            <option value="in">Dana Masuk</option>
            <option value="out">Dana Keluar</option>
          </select>
        </div>
        <div>
          <label className="label-cap block mb-1">Dari</label>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="px-2 py-1.5 border border-[#E4E4E7] text-xs font-mono bg-white" />
        </div>
        <div>
          <label className="label-cap block mb-1">Sampai</label>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="px-2 py-1.5 border border-[#E4E4E7] text-xs font-mono bg-white" />
        </div>
      </div>

      <div className="bg-white border border-[#E4E4E7] overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead>
            <tr className="border-b border-[#E4E4E7]">
              <th className="text-left label-cap px-4 py-3">Tanggal</th>
              <th className="text-left label-cap px-4 py-3">Jenis</th>
              <th className="text-left label-cap px-4 py-3">Kategori</th>
              <th className="text-left label-cap px-4 py-3">Catatan</th>
              <th className="text-left label-cap px-4 py-3">Order Terkait</th>
              <th className="text-right label-cap px-4 py-3">Jumlah</th>
              <th className="text-right label-cap px-4 py-3">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-sm text-[#52525B]">Belum ada data Petty Cash Medan.</td></tr>}
            {filtered.map((e) => {
              const auto = e.auto_source === "order";
              return (
                <tr key={e.id} className="border-b border-[#E4E4E7] row-hover" data-testid={`pcm-row-${e.id}`}>
                  <td className="px-4 py-3 text-sm font-mono">{e.date}</td>
                  <td className="px-4 py-3">
                    {e.type === "in"
                      ? <span className="text-[10px] font-bold uppercase tracking-wider bg-[#059669]/10 text-[#059669] border border-[#059669]/30 px-1.5 py-0.5">Masuk{auto ? " · Order" : ""}</span>
                      : e.category === setorCat
                        ? <span className="text-[10px] font-bold uppercase tracking-wider bg-[#002FA7]/10 text-[#002FA7] border border-[#002FA7]/30 px-1.5 py-0.5">Setor</span>
                        : <span className="text-[10px] font-bold uppercase tracking-wider bg-[#DC2626]/10 text-[#DC2626] border border-[#DC2626]/30 px-1.5 py-0.5">Keluar</span>}
                    {e.created_via === "wa_pcm_bot" && <span className="ml-1 text-[10px] font-bold uppercase tracking-wider bg-[#25D366]/10 text-[#128C7E] border border-[#25D366]/40 px-1.5 py-0.5" title={e.created_by}>WA</span>}
                  </td>
                  <td className="px-4 py-3 text-sm font-bold">{e.category || "—"}</td>
                  <td className="px-4 py-3 text-sm text-[#52525B] max-w-xs truncate">{e.note}</td>
                  <td className="px-4 py-3 text-xs font-mono">{entryOrderLabel(e)}</td>
                  <td className={`px-4 py-3 text-sm font-mono font-bold text-right ${e.type === "in" ? "text-[#059669]" : "text-[#DC2626]"}`}>
                    {e.type === "in" ? "+" : "−"}{formatIDR(e.amount)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {auto ? (
                      <span className="text-[10px] text-[#52525B]" title="Dikelola dari order (pemegang uang / status pembayaran)">otomatis</span>
                    ) : (
                      <div className="inline-flex gap-1">
                        <button onClick={() => openEdit(e)} className="p-2 hover:bg-[#F4F4F5] border border-[#E4E4E7]" data-testid={`pcm-edit-${e.id}`}><Pencil size={14} /></button>
                        <button onClick={() => setConfirmDel(e)} className="p-2 hover:bg-[#DC2626]/10 text-[#DC2626] border border-[#E4E4E7]" data-testid={`pcm-delete-${e.id}`}><Trash2 size={14} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-[#52525B] mt-2">Dana masuk "Order" tercatat otomatis saat pemegang uang tunai di order dipilih "Pak Syahlan" (Lunas = total, DP = nominal DP).</p>

      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border border-[#E4E4E7] w-full max-w-xl max-h-[92vh] overflow-y-auto" data-testid="pcm-form">
            <div className="px-6 py-4 border-b border-[#E4E4E7]">
              <div className="label-cap">{editingId ? "Ubah" : "Tambah"} {form.type === "in" ? "Dana Masuk (Manual)" : form.category === setorCat ? "Setoran ke Pusat" : "Dana Keluar"}</div>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <F label="Tanggal" required>
                <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={ic + " font-mono"} data-testid="pcm-date" />
              </F>
              <F label="Jumlah (IDR)" required>
                <input type="number" required min="1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={ic + " font-mono"} data-testid="pcm-amount" />
              </F>
              {form.type === "out" && (
                <F label="Kategori" required>
                  <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={ic} data-testid="pcm-category">
                    <option value="">— Pilih kategori —</option>
                    {cats.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </F>
              )}
              <F label="Order Terkait (opsional)" colSpan={form.type === "in"}>
                <select value={form.order_id} onChange={(e) => setForm({ ...form, order_id: e.target.value })} className={ic} data-testid="pcm-order-select">
                  <option value="">— Tidak terkait order —</option>
                  {orders.map((o) => <option key={o.id} value={o.id}>{o.order_no} · {o.customer?.name}</option>)}
                </select>
              </F>
              <F label="Catatan" colSpan>
                <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={ic} data-testid="pcm-note" />
              </F>
              {form.type === "out" && (
                <p className="col-span-2 text-[10px] text-[#52525B]">
                  {form.category === setorCat ? "Setoran ke pusat mengurangi saldo Pak Syahlan, tidak dicatat sebagai pengeluaran." : "Dana keluar otomatis tercatat juga di menu Pengeluaran."}
                </p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-[#E4E4E7] flex justify-end gap-2">
              <button type="button" onClick={() => setModal(false)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button type="submit" disabled={saving} className="px-4 py-2 text-xs font-bold uppercase bg-[#002FA7] text-white hover:bg-[#00227A] disabled:opacity-50" data-testid="pcm-submit">{saving ? "Menyimpan…" : "Simpan"}</button>
            </div>
          </form>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setConfirmDel(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E4E4E7] max-w-sm w-full p-6">
            <h3 className="font-heading text-lg font-bold mb-2">Hapus {confirmDel.type === "in" ? "Dana Masuk" : "Dana Keluar"}?</h3>
            <p className="text-sm text-[#52525B] mb-2">{confirmDel.date} · {confirmDel.category ? `${confirmDel.category} · ` : ""}{formatIDR(confirmDel.amount)}</p>
            {confirmDel.type === "out" && confirmDel.category !== setorCat && <p className="text-xs text-[#52525B] mb-6">Entri terkait di menu Pengeluaran juga ikut terhapus.</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button onClick={remove} className="px-4 py-2 text-xs font-bold uppercase bg-[#DC2626] text-white" data-testid="pcm-confirm-delete">Hapus</button>
            </div>
          </div>
        </div>
      )}

      {pwdModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setPwdModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={changePassword} className="bg-white border border-[#E4E4E7] max-w-sm w-full" data-testid="pcm-password-form">
            <div className="px-6 py-4 border-b border-[#E4E4E7]">
              <div className="label-cap">Ganti Password Petty Cash Medan</div>
              {!data.own_password && <p className="text-[10px] text-[#52525B] mt-1">Saat ini masih memakai password pengaturan.</p>}
            </div>
            <div className="p-6 space-y-3">
              <input type="password" required placeholder="Password saat ini" value={pwdForm.current_password} onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })} className={ic + " font-mono"} />
              <input type="password" required minLength={6} placeholder="Password baru (min 6)" value={pwdForm.new_password} onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })} className={ic + " font-mono"} />
              <input type="password" required minLength={6} placeholder="Ulangi password baru" value={pwdForm.confirm} onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })} className={ic + " font-mono"} />
            </div>
            <div className="px-6 py-4 border-t border-[#E4E4E7] flex justify-end gap-2">
              <button type="button" onClick={() => setPwdModal(false)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button type="submit" className="px-4 py-2 text-xs font-bold uppercase bg-[#002FA7] text-white hover:bg-[#00227A]">Simpan</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
};

const ic = "w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] bg-white";
const F = ({ label, children, required, colSpan }) => (
  <div className={colSpan ? "col-span-2" : ""}>
    <label className="label-cap block mb-1.5">{label}{required && <span className="text-[#DC2626]">*</span>}</label>
    {children}
  </div>
);

export default PettyCashMedan;
